import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "../hooks/useToast";
import ToastContainer from "../components/ToastContainer";
import { getErrorMessage } from "../utils/errorHandler";
import {
  ArrowLeft,
  User,
  FileText,
  Calendar,
  Mail,
  Download,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  Settings,
  RefreshCw,
  Shield,
} from "lucide-react";

import Step1 from "./Registration_2/RegisterForm";
import Step2 from "./Registration_2/RegisterPage1";
import Step3 from "./Registration_2/RegisterPage2";
import Step4 from "./Registration_2/RegisterPage3";
import Step5 from "./Registration_2/RegisterPage4";
import AddDocumentForm from "./Admin/AddDocument";
import Notes from "./Admin/notes";
import ScheduledS from "./Admin/Schedule";
import CustomEmail from "./Admin/CustomEmail";
import ProfileUp from "../assets/ProfileUp";

const steps = [Step1, Step2, Step3, Step4, Step5];

const TabButton = ({ active, onClick, icon: Icon, label, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all duration-200 text-sm ${
      active
        ? "bg-blue-600 text-white shadow-sm"
        : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
    }`}
  >
    <Icon size={16} />
    <span className="hidden sm:inline">{label}</span>
    {count !== undefined && (
      <span
        className={`text-xs px-1.5 py-0.5 rounded-full min-w-5 text-center ${
          active ? "bg-white/20" : "bg-gray-100 text-gray-600"
        }`}
      >
        {count}
      </span>
    )}
  </button>
);

const CollapsibleSection = ({
  title,
  children,
  defaultOpen = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-lg shadow-xs border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} className="text-gray-500" />}
          <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
        </div>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-gray-200"
          >
            <div className="p-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function RegistrationPage({ onClose }) {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState("profile");
  const [regStep, setRegStep] = useState(1);
  const [statuses, setStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [studentId, setStudentId] = useState(null);
  const [formData, setFormData] = useState({});
  const [studentData, setStudentData] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [userId, setUserId] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState("");
  const [userRole, setUserRole] = useState(null);

  const navigate = useNavigate();
  const initializationRef = useRef(false);

  // Initialize user role and student ID from localStorage - ONLY ONCE
  useEffect(() => {
    if (initializationRef.current) return;
    initializationRef.current = true;

    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        const user = JSON.parse(userString);
        setUserRole(user.role || null);
      }

      const storedId = localStorage.getItem("student_id");
      if (storedId) {
        setStudentId(storedId);
      } else {
        console.warn("No student_id found in localStorage");
        setIsDataLoaded(true);
      }
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
      setIsDataLoaded(true);
    }
  }, []);

  // Check if user has admin role - FIXED: Proper boolean logic
  const isAdmin = userRole === "Admin" || userRole === "Sub Admin";

  // Enhanced data fetching function - OPTIMIZED to prevent loops
  const fetchAllData = useCallback(
    async (showRefreshIndicator = false) => {
      if (!studentId) {
        setIsDataLoaded(true);
        setLoading(false);
        return;
      }

      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const token = localStorage.getItem("token");

      try {
        // Common headers with JWT
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const [profileRes, documentsRes, statusesRes, photoRes] =
          await Promise.all([
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/get-student-full-profile/${studentId}`,
              { headers }
            ),
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/catalog/get-student-documents/${studentId}`,
              { headers }
            ),
            isAdmin
              ? fetch(`${process.env.REACT_APP_BACKEND_URL}/api/statuses`, {
                  headers,
                })
              : Promise.resolve(null),
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/profile-photo/${studentId}`,
              { headers }
            ),
          ]);

        // Profile
        if (profileRes && profileRes.ok) {
          const profileData = await profileRes.json();
          setStudentData(profileData);
          setFormData(profileData.form_data || profileData);
        } else if (profileRes) {
          throw new Error(`Profile fetch failed: ${profileRes.status}`);
        }

        // Documents
        if (documentsRes && documentsRes.ok) {
          const documentsData = await documentsRes.json();
          setUserId(documentsData.user_id);
          setDocuments(documentsData.documents || []);
          setStudentName(documentsData.student_name || "Unknown Student");
        } else if (documentsRes) {
          throw new Error(`Documents fetch failed: ${documentsRes.status}`);
        }

        // Statuses - Only fetch if admin
        if (isAdmin && statusesRes && statusesRes.ok) {
          const statusesData = await statusesRes.json();
          setStatuses(statusesData || []);
        } else {
          setStatuses([]);
        }

        // Photo
        if (photoRes && photoRes.ok) {
          const photoData = await photoRes.json();
          if (photoData.success && photoData.profile_photo) {
            setProfilePhoto(photoData.profile_photo);
            setStatus(photoData.status);
          }
        }

        if (showRefreshIndicator) {
          toast.success("Data refreshed successfully!");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        const errorMsg = getErrorMessage(error, "Failed to load student data");
        toast.error(errorMsg);
      } finally {
        setIsDataLoaded(true);
        setLoading(false);
        setRefreshing(false);
      }
    },
    [studentId, isAdmin, toast]
  );

  // Initial data fetch - ONLY when studentId is set
  useEffect(() => {
    if (studentId) {
      fetchAllData();
    }
  }, [studentId]);

  // Enhanced refresh function
  const handleManualRefresh = () => {
    fetchAllData(true);
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/delete-document/${documentId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setDocuments(prev => prev.filter(d => d.id !== documentId));
      toast.success("Document deleted successfully!");
      setDeleteConfirm(null);
    } catch (error) {
      const errorMsg = getErrorMessage(error, "Failed to delete document");
      toast.error(errorMsg);
      setDeleteConfirm(null);
    }
  };

  const exportStudentData = async () => {
    if (!studentId) {
      toast.error("Student ID is missing");
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/download_student/${studentId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!res.ok) throw new Error(`Export failed with status ${res.status}`);

      const contentDisposition = res.headers.get("content-disposition") || "";
      const fileNameMatch = contentDisposition.match(
        /filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i
      );
      const serverFileName = fileNameMatch
        ? decodeURIComponent(
            (fileNameMatch[1] || fileNameMatch[2] || fileNameMatch[3] || "")
              .trim()
              .replace(/^"|"$/g, "")
          )
        : null;

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = serverFileName || `student_data_${studentId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Excel downloaded successfully!");
    } catch (error) {
      const errorMsg = getErrorMessage(error, "Failed to download Excel");
      toast.error(errorMsg);
    }
  };

  const handleStatusApply = async () => {
    if (!studentId || !selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/students/${studentId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ status: Number(selectedStatus) }),
        }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Refresh status without full data fetch
      const statusName = statuses.find(s => s.id === Number(selectedStatus))?.status || "Status";
      setStatus(statusName);
      toast.success("Status updated successfully!");
      setSelectedStatus("");
    } catch (error) {
      const errorMsg = getErrorMessage(error, "Failed to update status");
      toast.error(errorMsg);
    }
  };

  const handleStepNavigation = (direction) => {
    if (direction === "next") {
      setRegStep((s) => Math.min(s + 1, steps.length));
    } else {
      setRegStep((s) => Math.max(s - 1, 1));
    }
  };

  const handleStepDataUpdate = (stepData) => {
    setFormData((prev) => ({ ...prev, ...stepData }));
  };

  // Enhanced document addition handler
  const handleDocumentAdded = async (newDoc) => {
    setDocuments(prev => [...prev, newDoc]);
    toast.success("Document added successfully!");
  };

  // Enhanced form submission handler
  const handleFormSubmit = async (data) => {
    handleStepDataUpdate(data);
    toast.success("Profile updated successfully!");
  };

  // Enhanced form next handler - RegisterForm manages its own steps internally
  const handleFormNext = (data) => {
    handleStepDataUpdate(data);
    // Don't manage regStep here - let RegisterForm handle its internal step navigation
    // RegisterForm manages steps 1-4 internally and calls onSubmit when complete
  };

  // Enhanced form back handler - RegisterForm manages backward navigation
  const handleFormBack = () => {
    // Let RegisterForm handle its own backward navigation internally
    // No need to manage regStep
  };

  if (!isDataLoaded || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading student data...</p>
        </div>
      </div>
    );
  }

  const CurrentStep = steps[regStep - 1];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Container */}
      <ToastContainer toasts={toast.toasts} onRemove={toast.remove} />

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-5 max-w-sm w-full shadow-lg"
            >
              <h3 className="text-base font-semibold text-gray-800 mb-3">
                Delete Document
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Are you sure you want to delete "{deleteConfirm.name}"? This
                action cannot be undone.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteDocument(deleteConfirm.id)}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-white shadow-xs border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Left side - Back button and profile */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const keysToKeep = ["adminActiveSection", "token", "user"];

                  // Loop through localStorage and remove all except the keys we want to keep
                  Object.keys(localStorage).forEach((key) => {
                    if (!keysToKeep.includes(key)) {
                      localStorage.removeItem(key);
                    }
                  });

                  if (onClose) onClose();
                  else navigate("/dashboard");
                }}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft size={18} />
                <span className="hidden sm:inline text-sm">Back to Dashboard</span>
              </button>

              <div className="flex items-center gap-2">
                {/* Profile Photo */}
                <div className="relative cursor-pointer">
                  <ProfileUp userId={studentId} />
                </div>
                {/* Student Info */}
                <div>
                  <h1 className="font-semibold text-gray-800 text-sm">
                    {studentName || "Student Profile"}
                  </h1>
                  <p className="text-xs text-gray-500">
                    ID: {studentId || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Right side - Status, refresh, and actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
              {/* User Role Badge */}
              {userRole && (
                <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg">
                  <Shield size={12} className="text-gray-600" />
                  <span className="text-xs font-medium text-gray-700">
                    {userRole}
                  </span>
                </div>
              )}

              {/* Manual Refresh Button */}
              <button
                onClick={handleManualRefresh}
                disabled={refreshing}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm ${
                  refreshing ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title="Refresh data"
              >
                <RefreshCw
                  size={14}
                  className={refreshing ? "animate-spin" : ""}
                />
                <span className="hidden sm:inline">
                  {refreshing ? "Refreshing..." : "Refresh"}
                </span>
              </button>

              {/* Status Update - Only show for Admin */}
              {isAdmin ? (
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Current Status Badge */}
                  {status && (
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        {
                          "Incomplete Application":
                            "bg-yellow-100 text-yellow-800",
                          "Complete Application": "bg-green-100 text-green-600",
                          "Conditional Offer": "bg-blue-100 text-blue-600",
                          Rejected: "bg-red-100 text-red-600",
                          Defer: "bg-purple-100 text-purple-600",
                          "Unconditional Offer": "bg-green-200 text-green-700",
                          Enroll: "bg-blue-100 text-blue-600",
                          Submitted: "bg-blue-50 text-blue-600",
                          Cancel: "bg-orange-200 text-orange-700",
                          "Not Eligible": "bg-pink-100 text-pink-600",
                          "SFE Exhausted": "bg-orange-100 text-orange-600",
                          Alumni: "bg-gray-300 text-gray-800",
                        }[status] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {status}
                    </span>
                  )}

                  {/* Dropdown and Apply - Only for Admin */}
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedStatus || ""}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-32"
                    >
                      <option value="">Update Status</option>
                      {statuses.map((st) => (
                        <option key={st.id} value={st.id}>
                          {st.status}
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={handleStatusApply}
                      disabled={!selectedStatus}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                        selectedStatus
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              ) : (
                // Show read-only status for non-admin users
                status && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 font-medium">
                      Status:
                    </span>
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        {
                          "Incomplete Application":
                            "bg-yellow-100 text-yellow-800",
                          "Complete Application": "bg-green-100 text-green-600",
                          "Conditional Offer": "bg-blue-100 text-blue-600",
                          Rejected: "bg-red-100 text-red-600",
                          Defer: "bg-purple-100 text-purple-600",
                          "Unconditional Offer": "bg-green-200 text-green-700",
                          Enroll: "bg-blue-100 text-blue-600",
                          Submitted: "bg-blue-50 text-blue-600",
                          Cancel: "bg-orange-200 text-orange-700",
                          "Not Eligible": "bg-pink-100 text-pink-600",
                          "SFE Exhausted": "bg-orange-100 text-orange-600",
                          Alumni: "bg-gray-300 text-gray-800",
                        }[status] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {status}
                    </span>
                  </div>
                )
              )}

              {/* Export Button */}
              <button
                onClick={exportStudentData}
                className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                <Download size={14} />
                <span className="hidden sm:inline">Export</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Photo Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.img
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              src={profilePhoto}
              alt="Full Size Profile"
              className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-1.5 mb-4 bg-gray-100 p-1.5 rounded-lg">
          <TabButton
            active={activeTab === "profile"}
            onClick={() => setActiveTab("profile")}
            icon={User}
            label="Profile"
          />
          <TabButton
            active={activeTab === "documents"}
            onClick={() => setActiveTab("documents")}
            icon={FileText}
            label="Documents"
            count={documents.length}
          />
          <TabButton
            active={activeTab === "schedule"}
            onClick={() => setActiveTab("schedule")}
            icon={Calendar}
            label="Schedule"
          />
          <TabButton
            active={activeTab === "communication"}
            onClick={() => setActiveTab("communication")}
            icon={Mail}
            label="Communication"
          />
          {/* Admin Tools Tab - Only show for Admin */}
          {isAdmin && (
            <TabButton
              active={activeTab === "admin"}
              onClick={() => setActiveTab("admin")}
              icon={Settings}
              label="Admin Tools"
            />
          )}
        </div>

        {/* Tab Content */}
        <div className="space-y-4">
          {activeTab === "profile" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-xs"
            >
              {CurrentStep && (
                <CurrentStep
                  isEmbedded={true}
                  student_id={studentId}
                  initialData={studentData}
                  formData={formData}
                  onNext={handleFormNext}
                  onBack={handleFormBack}
                  onSubmit={handleFormSubmit}
                  onDataChange={handleStepDataUpdate}
                />
              )}
            </motion.div>
          )}

          {activeTab === "documents" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              {/* Documents List */}
              <CollapsibleSection
                title={`Documents (${documents.length})`}
                icon={FileText}
                defaultOpen={true}
              >
                {documents.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <FileText
                      size={32}
                      className="mx-auto mb-3 text-gray-300"
                    />
                    <p className="text-sm">No documents uploaded yet</p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {documents.map((doc, index) => {
                      const fileName =
                        doc.document_name?.split("/").pop() || "Unknown file";
                      const documentUrl = doc.url;

                      return (
                        <div
                          key={doc.id || index}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex-1 min-w-0">
                            <a
                              href={documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline font-medium text-sm truncate block"
                            >
                              {fileName}
                            </a>
                            <div className="text-xs text-gray-500 mt-1">
                              {doc.document_type?.replace("_", " ") || "Unknown type"} •{" "}
                              {doc.created_at
                                ? new Date(doc.created_at).toLocaleDateString()
                                : "Unknown date"}
                            </div>
                          </div>
                          {/* Only show delete button for Admin */}
                          {isAdmin && (
                            <button
                              onClick={() =>
                                setDeleteConfirm({ id: doc.id, name: fileName })
                              }
                              className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded ml-2 flex-shrink-0"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CollapsibleSection>

              {/* Add Document - Only show for Admin */}
              {isAdmin && (
                <CollapsibleSection
                  title="Add New Document"
                  defaultOpen={documents.length === 0}
                >
                  <AddDocumentForm
                    studentId={studentId}
                    onDocumentAdded={handleDocumentAdded}
                  />
                </CollapsibleSection>
              )}
            </motion.div>
          )}

          {activeTab === "schedule" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <CollapsibleSection
                title="Interview Scheduling"
                icon={Calendar}
                defaultOpen={true}
              >
                <ScheduledS onScheduleUpdate={() => fetchAllData(false)} />
              </CollapsibleSection>
            </motion.div>
          )}

          {activeTab === "communication" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <CollapsibleSection
                title="Send Custom Email"
                icon={Mail}
                defaultOpen={true}
              >
                <CustomEmail
                  onEmailSent={() => {
                    toast.success("Email sent successfully!");
                  }}
                />
              </CollapsibleSection>
            </motion.div>
          )}

          {/* Admin Tab - Only show for Admin */}
          {isAdmin && activeTab === "admin" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <CollapsibleSection
                title="Administrative Notes"
                icon={Settings}
                defaultOpen={true}
              >
                <Notes
                  studentId={studentId}
                  onNotesUpdate={() => fetchAllData(false)}
                />
              </CollapsibleSection>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}